import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Switch } from '@/shared/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/shared/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow } from
"@/shared/components/ui/table";
import LeafletMap from '../components/maps/LeafletMap';
import GoogleMap from '@/shared/components/maps/GoogleMap';
import {
  MapPin,
  Plus,
  Pencil,
  Trash2,
  Circle,
  Pentagon,
  AlertTriangle,
  Bell,
  BellRing,
  CheckCircle2,
  XCircle,
  Clock,
  Truck,
  RefreshCw,
  Settings,
  Mail,
  MessageSquare,
  Map as MapIcon,
  Layers,
  Eye,
  EyeOff,
  Download,
  Upload,
  Key,
  ArrowLeftRight } from
'lucide-react';
import { toast } from 'sonner';

const API_URL = API_BASE || '';
const ezerpToken = localStorage.getItem('ezerpToken');

const Geofencing = () => {const { t } = useTranslation("wms");
  // State
  const [mapProvider, setMapProvider] = useState('leaflet'); // 'leaflet' or 'google'
  const [googleApiKey, setGoogleApiKey] = useState(localStorage.getItem('google_maps_api_key') || '');
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);

  useEffect(() => {
    const fetchApiKey = async () => {
      try {
        const res = await fetch(`${API_URL}/api/api-keys/google_maps_api_key?include_value=true`, {
          headers: { 'Authorization': `Bearer ${ezerpToken}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.has_value && data.value) {
            setGoogleApiKey(data.value);
            localStorage.setItem('google_maps_api_key', data.value);
          }
        }
      } catch (error) {
        console.error('Failed to fetch API key from backend:', error);
      }
    };
    fetchApiKey();
  }, []);

  // Zone categories and alert types from API
  const [zoneCategories, setZoneCategories] = useState([]);
  const [alertTypes, setAlertTypes] = useState([]);

  const [zones, setZones] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [alerts, setAlerts] = useState([]);

  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingType, setDrawingType] = useState('circle');
  const [drawingPoints, setDrawingPoints] = useState([]);

  const [showZoneDialog, setShowZoneDialog] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [zoneForm, setZoneForm] = useState({
    name: '',
    category: 'delivery',
    type: 'circle',
    radius: 500,
    alertOnEntry: true,
    alertOnExit: true,
    alertOnDwell: false,
    dwellMinutes: 30,
    sendEmail: false,
    sendSms: false,
    emailRecipients: '',
    smsRecipients: ''
  });

  const [activeTab, setActiveTab] = useState('map');
  const [showZones, setShowZones] = useState(true);
  const [showVehicles, setShowVehicles] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);

  const wsRef = React.useRef(null);

  // Fetch reference data from API
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const headers = {
          'Authorization': `Bearer ${ezerpToken}`,
          'Content-Type': 'application/json'
        };
        const [zoneTypesRes, alertTypesRes] = await Promise.all([
        fetch(`${API_URL}/api/reference/zone-types`, { headers }),
        fetch(`${API_URL}/api/reference/alert-types`, { headers })]
        );

        if (zoneTypesRes.ok) {
          const data = await zoneTypesRes.json();
          setZoneCategories(data.map((z) => ({
            id: z.code.toLowerCase(),
            name: z.name,
            color: z.color || '#6366f1',
            icon: z.color ? getColorEmoji(z.color) : '🔵'
          })));
        }

        if (alertTypesRes.ok) {
          const data = await alertTypesRes.json();
          setAlertTypes(data.map((a) => ({
            id: a.code.toLowerCase(),
            name: a.name,
            description: a.description || `Alert: ${a.name}`
          })));
        }
      } catch (error) {
        console.error('Failed to fetch reference data:', error);
        // Fallback
        setZoneCategories([
        { id: 'warehouse', name: 'Warehouse/Hub', color: '#22C55E', icon: '🟢' },
        { id: 'restricted', name: 'Restricted Area', color: '#EF4444', icon: '🔴' },
        { id: 'delivery', name: 'Delivery Zone', color: '#F59E0B', icon: '🟡' },
        { id: 'service', name: 'Service Area', color: '#3B82F6', icon: '🔵' }]
        );
        setAlertTypes([
        { id: 'entry', name: 'Entry Alert', description: 'When vehicle enters zone' },
        { id: 'exit', name: 'Exit Alert', description: 'When vehicle leaves zone' },
        { id: 'dwell', name: 'Dwell Alert', description: 'When vehicle stays too long' }]
        );
      }
    };
    fetchReferenceData();
  }, []);

  // Helper to get emoji from color
  const getColorEmoji = (color) => {
    const colorMap = {
      '#22C55E': '🟢', '#10B981': '🟢', '#3B82F6': '🔵', '#2563EB': '🔵',
      '#EF4444': '🔴', '#DC2626': '🔴', '#F59E0B': '🟡', '#FBBF24': '🟡',
      '#8B5CF6': '🟣', '#06B6D4': '🔵'
    };
    return colorMap[color] || '⚪';
  };

  // WebSocket connection for real-time updates
  useEffect(() => {
    const connectWebSocket = () => {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = API_URL.replace(/^https?:\/\//, '');
      const wsUrl = `${wsProtocol}//${wsHost}/api/ws/geofencing?token=${ezerpToken}`;

      try {
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
          console.log('[Geofencing] WebSocket connected');
          setWsConnected(true);
        };

        wsRef.current.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            handleWebSocketMessage(message);
          } catch (error) {
            console.error('[Geofencing] Failed to parse message:', error);
          }
        };

        wsRef.current.onclose = () => {
          console.log('[Geofencing] WebSocket disconnected');
          setWsConnected(false);
          // Reconnect after 5 seconds
          setTimeout(connectWebSocket, 5000);
        };

        wsRef.current.onerror = (error) => {
          console.error('[Geofencing] WebSocket error:', error);
        };
      } catch (error) {
        console.error('[Geofencing] Failed to connect WebSocket:', error);
        setTimeout(connectWebSocket, 5000);
      }
    };

    // Initial data load
    loadData();

    // Connect WebSocket
    connectWebSocket();

    // Fallback polling every 30 seconds (in case WS fails)
    const interval = setInterval(loadData, 30000);

    return () => {
      clearInterval(interval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Handle WebSocket messages
  const handleWebSocketMessage = (message) => {
    if (message.type === 'vehicle_update') {
      // Update single vehicle position
      setVehicles((prev) => prev.map((v) =>
      v.id === message.vehicle_id ?
      {
        ...v,
        location: message.location,
        speed: message.speed,
        status: message.status
      } :
      v
      ));
    } else if (message.type === 'geofence_alert') {
      // Add new alert
      const newAlert = {
        id: `live-${Date.now()}`,
        type: message.alert_type,
        zone: message.zone_name,
        zoneId: message.zone_id,
        vehicle: message.vehicle_name,
        vehicleId: message.vehicle_id,
        driver: message.driver_name,
        timestamp: message.timestamp,
        acknowledged: false,
        isLive: true
      };
      setAlerts((prev) => [newAlert, ...prev]);

      // Show toast notification
      toast.warning(`${message.alert_type} Alert`, {
        description: `${message.vehicle_name} ${message.alert_type} zone ${message.zone_name}`,
        duration: 10000
      });
    }
  };

  const loadData = async () => {
    const headers = {
      'Authorization': `Bearer ${ezerpToken}`,
      'Content-Type': 'application/json'
    };
    try {
      const [zonesRes, vehiclesRes, alertsRes] = await Promise.all([
      fetch(`${API_URL}/api/geofencing/zones`, { headers }),
      fetch(`${API_URL}/api/geofencing/vehicles`, { headers }),
      fetch(`${API_URL}/api/geofencing/alerts?acknowledged=false`, { headers })]
      );

      if (zonesRes.ok) {
        const zonesData = await zonesRes.json();
        setZones(zonesData.zones.map((z) => ({
          id: z.id,
          name: z.name,
          category: z.category,
          type: z.type,
          center: z.center,
          radius: z.radius,
          points: z.points,
          alertOnEntry: z.alertOnEntry,
          alertOnExit: z.alertOnExit,
          alertOnDwell: z.alertOnDwell,
          dwellMinutes: z.dwellMinutes,
          sendEmail: z.sendEmail,
          sendSms: z.sendSms,
          emailRecipients: z.emailRecipients,
          smsRecipients: z.smsRecipients
        })));
      }

      if (vehiclesRes.ok) {
        const vehiclesData = await vehiclesRes.json();
        setVehicles(vehiclesData.vehicles.map((v) => ({
          id: v.id,
          name: v.name,
          driver: v.driverName,
          status: v.status,
          speed: v.speed,
          location: v.location,
          route: v.routeId
        })));
      }

      if (alertsRes.ok) {
        const alertsData = await alertsRes.json();
        setAlerts(alertsData.alerts.map((a) => ({
          id: a.id,
          type: a.type,
          zone: a.zoneName,
          zoneId: a.zoneId,
          vehicle: a.vehicleName,
          vehicleId: a.vehicleId,
          driver: a.driverName,
          timestamp: a.timestamp,
          acknowledged: a.acknowledged,
          dwellTime: a.dwellMinutes
        })));
      }
    } catch (error) {
      console.error('Failed to load geofencing data:', error);
    }
  };

  // Drawing handlers
  const handleStartDrawing = (type) => {
    setDrawingType(type);
    setDrawingPoints([]);
    setIsDrawing(true);
    toast.info(`Click on map to draw ${type}`, {
      description: type === 'polygon' ? 'Click multiple points, then save' : 'Click to set center point'
    });
  };

  const handlePointAdd = (point) => {
    if (drawingType === 'circle') {
      setDrawingPoints([point]);
      // Auto-open zone dialog for circle
      setZoneForm((prev) => ({ ...prev, type: 'circle' }));
      setShowZoneDialog(true);
      setIsDrawing(false);
    } else {
      setDrawingPoints((prev) => [...prev, point]);
    }
  };

  const handleFinishDrawing = () => {
    if (drawingType === 'polygon' && drawingPoints.length >= 3) {
      setZoneForm((prev) => ({ ...prev, type: 'polygon' }));
      setShowZoneDialog(true);
    }
    setIsDrawing(false);
  };

  const handleCancelDrawing = () => {
    setIsDrawing(false);
    setDrawingPoints([]);
  };

  // Zone CRUD - API calls
  const handleSaveZone = async () => {
    setIsLoading(true);
    try {
      const payload = {
        name: zoneForm.name,
        category: zoneForm.category,
        type: zoneForm.type,
        radius: zoneForm.radius,
        alertOnEntry: zoneForm.alertOnEntry,
        alertOnExit: zoneForm.alertOnExit,
        alertOnDwell: zoneForm.alertOnDwell,
        dwellMinutes: zoneForm.dwellMinutes,
        sendEmail: zoneForm.sendEmail,
        sendSms: zoneForm.sendSms,
        emailRecipients: zoneForm.emailRecipients || null,
        smsRecipients: zoneForm.smsRecipients || null,
        ...(zoneForm.type === 'circle' && drawingPoints.length === 1 ?
        { center: drawingPoints[0] } :
        {}),
        ...(zoneForm.type === 'polygon' && drawingPoints.length >= 3 ?
        { points: drawingPoints } :
        {})
      };

      const headers = {
        'Authorization': `Bearer ${ezerpToken}`,
        'Content-Type': 'application/json'
      };

      let response;
      if (editingZone) {
        response = await fetch(`${API_URL}/api/geofencing/zones/${editingZone.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${API_URL}/api/geofencing/zones`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        toast.success(editingZone ? 'Zone updated' : 'Zone created');
        loadData(); // Refresh data
      } else {
        toast.error('Failed to save zone');
      }
    } catch (error) {
      console.error('Error saving zone:', error);
      toast.error('Failed to save zone');
    } finally {
      setIsLoading(false);
      setShowZoneDialog(false);
      setEditingZone(null);
      setDrawingPoints([]);
      resetZoneForm();
    }
  };

  const handleEditZone = (zone) => {
    setEditingZone(zone);
    setZoneForm({
      name: zone.name,
      category: zone.category,
      type: zone.type,
      radius: zone.radius || 500,
      alertOnEntry: zone.alertOnEntry,
      alertOnExit: zone.alertOnExit,
      alertOnDwell: zone.alertOnDwell || false,
      dwellMinutes: zone.dwellMinutes || 30,
      sendEmail: zone.sendEmail || false,
      sendSms: zone.sendSms || false,
      emailRecipients: zone.emailRecipients || '',
      smsRecipients: zone.smsRecipients || ''
    });
    if (zone.type === 'circle' && zone.center) {
      setDrawingPoints([zone.center]);
    } else if (zone.type === 'polygon' && zone.points) {
      setDrawingPoints(zone.points);
    }
    setShowZoneDialog(true);
  };

  const handleDeleteZone = async (zoneId) => {
    try {
      const response = await fetch(`${API_URL}/api/geofencing/zones/${zoneId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${ezerpToken}` }
      });
      if (response.ok) {
        toast.success('Zone deleted');
        loadData();
      } else {
        toast.error('Failed to delete zone');
      }
    } catch (error) {
      console.error('Error deleting zone:', error);
      toast.error('Failed to delete zone');
    }
  };

  const resetZoneForm = () => {
    setZoneForm({
      name: '',
      category: 'delivery',
      type: 'circle',
      radius: 500,
      alertOnEntry: true,
      alertOnExit: true,
      alertOnDwell: false,
      dwellMinutes: 30,
      sendEmail: false,
      sendSms: false,
      emailRecipients: '',
      smsRecipients: ''
    });
  };

  // Alert handlers - API call
  const handleAcknowledgeAlert = async (alertId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/geofencing/alerts/${alertId}/acknowledge?acknowledged_by=admin`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${ezerpToken}` }
        }
      );
      if (response.ok) {
        toast.success('Alert acknowledged');
        loadData();
      } else {
        toast.error('Failed to acknowledge alert');
      }
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      toast.error('Failed to acknowledge alert');
    }
  };

  // Map provider toggle
  const handleMapProviderChange = (provider) => {
    if (provider === 'google' && !googleApiKey) {
      setShowApiKeyDialog(true);
      return;
    }
    setMapProvider(provider);
  };

  const handleSaveApiKey = async (key) => {
    localStorage.setItem('google_maps_api_key', key);
    setGoogleApiKey(key);
    setMapProvider('google');
    setShowApiKeyDialog(false);
    
    try {
      await fetch(`${API_URL}/api/api-keys/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${ezerpToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          key_name: 'google_maps_api_key',
          value: key
        })
      });
    } catch (error) {
      console.error('Failed to save API key to backend:', error);
    }
    
    toast.success('Google Maps API key saved');
  };

  // Stats
  const stats = {
    totalZones: zones.length,
    activeAlerts: alerts.filter((a) => !a.acknowledged).length,
    vehiclesInZone: vehicles.filter((v) => {
      // Simple check - in production would do proper geo calculation
      return zones.some((z) => z.category === 'warehouse');
    }).length,
    vehiclesOnline: vehicles.filter((v) => v.status !== 'offline').length
  };

  return (
    <div className="p-6 space-y-6" data-testid="geofencing-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("geofencing", "Geofencing")}</h1>
          <p className="text-muted-foreground">{t("monitorVehiclesAnd", "Monitor vehicles and manage geographic zones")}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Map Provider Toggle */}
          <div className="flex items-center gap-2 bg-muted p-1 rounded-lg">
            <Button
              variant={mapProvider === 'leaflet' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => handleMapProviderChange('leaflet')}
              className="gap-2">
              
              <Layers className="h-4 w-4" />{t("openstreetmap", "OpenStreetMap")}

            </Button>
            <Button
              variant={mapProvider === 'google' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => handleMapProviderChange('google')}
              className="gap-2">
              
              <MapIcon className="h-4 w-4" />{t("googleMaps", "Google Maps")}

            </Button>
          </div>

          {mapProvider === 'google' &&
          <Button variant="outline" size="sm" onClick={() => setShowApiKeyDialog(true)}>
              <Key className="h-4 w-4 mr-2" />{t("apiKey", "API Key")}

          </Button>
          }

          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />{t("refresh", "Refresh")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <MapPin className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalZones}</p>
                <p className="text-sm text-muted-foreground">{t("totalZones", "Total Zones")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={stats.activeAlerts > 0 ? 'border-red-200 bg-red-50 dark:bg-red-950/20' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${stats.activeAlerts > 0 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
                <BellRing className={`h-5 w-5 ${stats.activeAlerts > 0 ? 'text-red-600' : 'text-gray-600'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.activeAlerts}</p>
                <p className="text-sm text-muted-foreground">{t("activeAlerts", "Active Alerts")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <Truck className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.vehiclesOnline}</p>
                <p className="text-sm text-muted-foreground">{t("vehiclesOnline", "Vehicles Online")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <CheckCircle2 className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.vehiclesInZone}</p>
                <p className="text-sm text-muted-foreground">{t("inWarehouse", "In Warehouse")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Map */}
        <div className="col-span-2">
          <Card className="overflow-hidden">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <MapIcon className="h-5 w-5" />{t("liveMap", "Live Map")}

                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant={showZones ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => setShowZones(!showZones)}>
                    
                    {showZones ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}{t("zones", "Zones")}

                  </Button>
                  <Button
                    variant={showVehicles ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => setShowVehicles(!showVehicles)}>
                    
                    {showVehicles ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}{t("vehicles", "Vehicles")}

                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Drawing Controls */}
              {isDrawing ?
              <div className="p-3 bg-purple-50 dark:bg-purple-950/20 border-b flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-purple-500">{t("drawingMode", "Drawing Mode")}</Badge>
                    <span className="text-sm">
                      {drawingType === 'circle' ?
                    'Click to set center point' :
                    `${drawingPoints.length} points added`
                    }
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {drawingType === 'polygon' && drawingPoints.length >= 3 &&
                  <Button size="sm" onClick={handleFinishDrawing}>
                        <CheckCircle2 className="h-4 w-4 mr-1" />{t("finish", "Finish")}

                  </Button>
                  }
                    <Button variant="outline" size="sm" onClick={handleCancelDrawing}>
                      <XCircle className="h-4 w-4 mr-1" />{t("cancel", "Cancel")}

                  </Button>
                  </div>
                </div> :

              <div className="p-3 bg-muted/30 border-b flex items-center gap-2">
                  <span className="text-sm text-muted-foreground mr-2">{t("drawZone", "Draw zone:")}</span>
                  <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleStartDrawing('circle')}
                  className="gap-1">
                  
                    <Circle className="h-4 w-4" />{t("circle", "Circle")}

                </Button>
                  <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleStartDrawing('polygon')}
                  className="gap-1">
                  
                    <Pentagon className="h-4 w-4" />{t("polygon", "Polygon")}

                </Button>
                </div>
              }

              {/* Map */}
              <div className="h-[500px]">
                {mapProvider === 'leaflet' ?
                <LeafletMap
                  vehicles={showVehicles ? vehicles : []}
                  zones={showZones ? zones : []}
                  selectedVehicle={selectedVehicle}
                  onVehicleSelect={setSelectedVehicle}
                  selectedZone={selectedZone}
                  onZoneSelect={setSelectedZone}
                  isDrawing={isDrawing}
                  drawingType={drawingType}
                  drawingPoints={drawingPoints}
                  onPointAdd={handlePointAdd} /> :


                <GoogleMap
                  apiKey={googleApiKey}
                  markers={showVehicles ? vehicles : []}
                  zones={showZones ? zones : []}
                  selectedMarker={selectedVehicle}
                  onMarkerSelect={setSelectedVehicle}
                  selectedZone={selectedZone}
                  onZoneSelect={setSelectedZone}
                  isDrawing={isDrawing}
                  drawingType={drawingType}
                  drawingPoints={drawingPoints}
                  onPointAdd={handlePointAdd} />

                }
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-4">
          {/* Alerts */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between text-base">
                <span className="flex items-center gap-2">
                  <BellRing className="h-4 w-4" />{t("liveAlerts", "Live Alerts")}

                </span>
                {stats.activeAlerts > 0 &&
                <Badge variant="destructive">{stats.activeAlerts}{t("new", "new")}</Badge>
                }
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 max-h-60 overflow-y-auto">
              {alerts.filter((a) => !a.acknowledged).length === 0 ?
              <div className="p-4 text-center text-muted-foreground text-sm">{t("noActiveAlerts", "No active alerts")}

              </div> :

              alerts.filter((a) => !a.acknowledged).map((alert) =>
              <div key={alert.id} className="p-3 border-b last:border-0 hover:bg-muted/50">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant={alert.type === 'entry' ? 'destructive' : 'secondary'} className="text-xs">
                            {alert.type.toUpperCase()}
                          </Badge>
                          <span className="text-sm font-medium">{alert.zone}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {alert.vehicle} • {alert.driver}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(alert.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                      <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAcknowledgeAlert(alert.id)}>
                    
                        <CheckCircle2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
              )
              }
            </CardContent>
          </Card>

          {/* Zones List */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between text-base">
                <span className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />{t("zones", "Zones")}

                </span>
                <Badge variant="outline">{zones.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 max-h-60 overflow-y-auto">
              {zones.map((zone) => {
                const cat = zoneCategories.find((c) => c.id === zone.category);
                return (
                  <div
                    key={zone.id}
                    className={`p-3 border-b last:border-0 cursor-pointer hover:bg-muted/50 ${selectedZone?.id === zone.id ? 'bg-muted' : ''}`
                    }
                    onClick={() => setSelectedZone(zone)}>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span>{cat?.icon}</span>
                        <div>
                          <p className="text-sm font-medium">{zone.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {zone.type === 'circle' ? `${zone.radius}m radius` : `${zone.points?.length} points`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => {e.stopPropagation();handleEditZone(zone);}}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={(e) => {e.stopPropagation();handleDeleteZone(zone.id);}}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>);

              })}
            </CardContent>
          </Card>

          {/* Vehicles List */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between text-base">
                <span className="flex items-center gap-2">
                  <Truck className="h-4 w-4" />{t("vehicles", "Vehicles")}

                </span>
                <Badge variant="outline">{vehicles.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 max-h-48 overflow-y-auto">
              {vehicles.map((vehicle) =>
              <div
                key={vehicle.id}
                className={`p-3 border-b last:border-0 cursor-pointer hover:bg-muted/50 ${selectedVehicle?.id === vehicle.id ? 'bg-muted' : ''}`
                }
                onClick={() => setSelectedVehicle(vehicle)}>
                
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${vehicle.status === 'moving' ? 'bg-blue-500' :
                    vehicle.status === 'idle' ? 'bg-amber-500' :
                    vehicle.status === 'online' ? 'bg-green-500' : 'bg-gray-400'}`
                    } />
                      <div>
                        <p className="text-sm font-medium">{vehicle.name}</p>
                        <p className="text-xs text-muted-foreground">{vehicle.driver}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="text-xs">{vehicle.speed}{t("kmh", "km/h")}</Badge>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Zone Dialog */}
      <Dialog open={showZoneDialog} onOpenChange={setShowZoneDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingZone ? 'Edit Zone' : 'Create Zone'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("zoneName", "Zone Name")}</Label>
              <Input
                value={zoneForm.name}
                onChange={(e) => setZoneForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Enter zone name" />
              
            </div>

            <div className="space-y-2">
              <Label>{t("category", "Category")}</Label>
              <Select value={zoneForm.category} onValueChange={(v) => setZoneForm((prev) => ({ ...prev, category: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {zoneCategories.map((cat) =>
                  <SelectItem key={cat.id} value={cat.id}>
                      <span className="flex items-center gap-2">
                        {cat.icon} {cat.name}
                      </span>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {zoneForm.type === 'circle' &&
            <div className="space-y-2">
                <Label>{t("radiusMeters", "Radius (meters)")}</Label>
                <Input
                type="number"
                value={zoneForm.radius}
                onChange={(e) => setZoneForm((prev) => ({ ...prev, radius: parseInt(e.target.value) || 500 }))} />
              
              </div>
            }

            <div className="space-y-3 pt-2">
              <Label>{t("alertSettings", "Alert Settings")}</Label>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">{t("alertOnEntry", "Alert on Entry")}</span>
                  <Switch
                    checked={zoneForm.alertOnEntry}
                    onCheckedChange={(v) => setZoneForm((prev) => ({ ...prev, alertOnEntry: v }))} />
                  
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">{t("alertOnExit", "Alert on Exit")}</span>
                  <Switch
                    checked={zoneForm.alertOnExit}
                    onCheckedChange={(v) => setZoneForm((prev) => ({ ...prev, alertOnExit: v }))} />
                  
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">{t("alertOnDwell", "Alert on Dwell")}</span>
                  <Switch
                    checked={zoneForm.alertOnDwell}
                    onCheckedChange={(v) => setZoneForm((prev) => ({ ...prev, alertOnDwell: v }))} />
                  
                </div>
                {zoneForm.alertOnDwell &&
                <div className="pl-4">
                    <Label className="text-xs">{t("dwellTimeMinutes", "Dwell time (minutes)")}</Label>
                    <Input
                    type="number"
                    value={zoneForm.dwellMinutes}
                    onChange={(e) => setZoneForm((prev) => ({ ...prev, dwellMinutes: parseInt(e.target.value) || 30 }))}
                    className="mt-1" />
                  
                  </div>
                }
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <Label>{t("notifications", "Notifications")}</Label>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-2">
                    <Mail className="h-4 w-4" />{t("sendEmail", "Send Email")}

                  </span>
                  <Switch
                    checked={zoneForm.sendEmail}
                    onCheckedChange={(v) => setZoneForm((prev) => ({ ...prev, sendEmail: v }))} />
                  
                </div>
                {zoneForm.sendEmail &&
                <Input
                  placeholder="Email recipients (comma separated)"
                  value={zoneForm.emailRecipients}
                  onChange={(e) => setZoneForm((prev) => ({ ...prev, emailRecipients: e.target.value }))}
                  className="text-sm" />

                }
                <div className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />{t("sendSms", "Send SMS")}

                  </span>
                  <Switch
                    checked={zoneForm.sendSms}
                    onCheckedChange={(v) => setZoneForm((prev) => ({ ...prev, sendSms: v }))} />
                  
                </div>
                {zoneForm.sendSms &&
                <Input
                  placeholder="Phone numbers (comma separated)"
                  value={zoneForm.smsRecipients}
                  onChange={(e) => setZoneForm((prev) => ({ ...prev, smsRecipients: e.target.value }))}
                  className="text-sm" />

                }
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {setShowZoneDialog(false);setEditingZone(null);resetZoneForm();}}>{t("cancel", "Cancel")}

            </Button>
            <Button onClick={handleSaveZone} disabled={!zoneForm.name}>
              {editingZone ? 'Update Zone' : 'Create Zone'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Key Dialog */}
      <Dialog open={showApiKeyDialog} onOpenChange={setShowApiKeyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />{t("googleMapsApi", "Google Maps API Key")}

            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">{t("enterYourGoogle", "Enter your Google Maps API key to use Google Maps. Get one from the")}
              {' '}
              <a href="https://console.cloud.google.com/google/maps-apis" target="_blank" rel="noopener noreferrer" className="text-primary underline">{t("googleCloudConsole", "Google Cloud Console")}

              </a>
            </p>
            <Input
              type="password"
              placeholder="Enter API key"
              value={googleApiKey}
              onChange={(e) => setGoogleApiKey(e.target.value)} />
            
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApiKeyDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button onClick={() => handleSaveApiKey(googleApiKey)} disabled={!googleApiKey}>{t("saveUseGoogle", "Save & Use Google Maps")}

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

export default Geofencing;
